import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Marquee } from '@sorare/core/src/atoms/container/Marquee';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandM,
  BrandXL,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import defaultDialogBg from './assets/bg.webp';

const StyledDialog = styled(Dialog)`
  @media ${tabletAndAbove} {
    min-width: 870px;
  }
`;

const TopMarquee = styled.div`
  position: absolute;
  right: 0;
  left: 0;
  top: 0;
  & img {
    height: var(--triple-unit);
  }
`;

const BottomMarquee = styled(TopMarquee)`
  top: unset;
  bottom: 0;
`;

const Content = styled(Vertical)<{ $dialogBg: string }>`
  height: 100%;
  background: url(${props => props.$dialogBg}), #000;
  background-repeat: no-repeat;
  padding: 0 var(--triple-unit);
  position: relative;
  isolation: isolate;
  background-size: cover;
  @media ${tabletAndAbove} {
    min-height: 480px;
  }
`;

const LeftImg = styled.img`
  position: absolute;
  object-fit: contain;
  right: 100%;
  transform: translate(50%, -30%);
`;

const RightImg = styled.img`
  position: absolute;
  object-fit: contain;
  left: 100%;
  transform: translate(-60%, 20%);
`;

const MobileImg = styled.img`
  object-fit: contain;
`;

const TextContent = styled(Vertical)`
  align-self: stretch;
  justify-content: center;
`;

const textGradient = css`
  background: linear-gradient(
    to bottom,
    var(--c-white) 70%,
    var(--c-nd-50-opaque) 90%
  );
  text-align: center;
  background-size: 100% 1em;
  background-repeat: repeat-y;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const GreenText = styled(BrandM).attrs({
  bold: true,
})`
  color: var(--c-onBase-success);
`;

const MainText = styled(BrandXL)`
  ${textGradient}
  text-align: center;

  @media ${tabletAndAbove} {
    max-width: 80%;
  }
`;

const SecondaryText = styled(BrandM)`
  ${textGradient}
  text-align: center;
  max-width: 80%;
`;

export type Props = {
  onClose: () => void;
  dialogBg?: string;
  marqueeImgTop?: string;
  marqueeImgBottom?: string;
  leftImg: string;
  rightImg: string;
  mobileImg: string;
  iconsImg?: string;
  greenText: ReactNode;
  mainText: ReactNode;
  secondaryText: ReactNode;
  captionText?: ReactNode;
};
export const PickerOnboardingDialog = ({
  onClose,
  dialogBg = defaultDialogBg,
  marqueeImgTop,
  marqueeImgBottom,
  leftImg,
  rightImg,
  mobileImg,
  iconsImg,
  greenText,
  mainText,
  secondaryText,
  captionText,
}: Props) => {
  const isTablet = useIsTabletAndAbove();
  return (
    <StyledDialog
      open
      title={
        <LabelL className="text-center" bold>
          <FormattedMessage
            id="missions.daily.picker.onboarding.dialogTitle"
            defaultMessage="Daily Mission"
          />
        </LabelL>
      }
      onClose={onClose}
    >
      <Content gap={2} center $dialogBg={dialogBg}>
        {!!marqueeImgTop && (
          <TopMarquee>
            <Marquee direction="reverse" duration="10s">
              <img src={marqueeImgTop} alt="" />
            </Marquee>
          </TopMarquee>
        )}
        {!!marqueeImgBottom && (
          <BottomMarquee>
            <Marquee direction="reverse" duration="10s">
              <img src={marqueeImgBottom} alt="" />
            </Marquee>
          </BottomMarquee>
        )}
        {isTablet ? (
          <>
            <LeftImg src={leftImg} alt="xp" width={282} height={210} />
            <RightImg src={rightImg} alt="xp" width={282} height={210} />
          </>
        ) : (
          <MobileImg src={mobileImg} alt="xp" width={282} height={210} />
        )}
        <TextContent gap={2} center>
          <GreenText>{greenText}</GreenText>
          <MainText>{mainText}</MainText>
        </TextContent>
        <SecondaryText>{secondaryText}</SecondaryText>
        {!!iconsImg && <img src={iconsImg} width={300} alt="" />}
        <Vertical gap={2}>
          <Button color="secondary" size="large" onClick={onClose}>
            <FormattedMessage {...glossary.letsGo} />
          </Button>
          {!!captionText && (
            <LabelS color="var(--c-nd-600)">{captionText}</LabelS>
          )}
        </Vertical>
      </Content>
    </StyledDialog>
  );
};
